import { LOCAL_LANE_MANIFESTS } from "@/config/model-manifest";
import { createManifestAssetReader } from "@/runtime/local-manifest-assets";
import {
  createBrowserOnnxTtsRuntime,
  type BrowserOnnxTtsRuntime,
} from "@/vendor/openmoss-browser-onnx-runtime";

interface BuiltinVoice {
  voice: string;
  display_name?: string;
  group?: string;
}

export interface TtsAudioChunk {
  channels: number;
  chunkData: Float32Array[];
  sampleRate: number;
}

export interface TtsSynthesisResult {
  durationMs: number;
  text: string;
  voice: string;
  chunks: TtsAudioChunk[];
}

let runtimePromise: Promise<BrowserOnnxTtsRuntime> | null = null;

function chooseVoice(voices: BuiltinVoice[]): BuiltinVoice {
  return (
    voices.find((voice) => voice.voice === "Adam") ??
    voices.find((voice) => voice.display_name?.startsWith("EN")) ??
    voices.find((voice) => voice.group?.includes("English")) ??
    voices[0]
  );
}

async function getRuntime(): Promise<BrowserOnnxTtsRuntime> {
  if (!runtimePromise) {
    runtimePromise = (async () => {
      const runtime = createBrowserOnnxTtsRuntime({
        assetReader: createManifestAssetReader(LOCAL_LANE_MANIFESTS.tts),
      });
      await runtime.configure({
        modelPath: LOCAL_LANE_MANIFESTS.tts.id,
        threadCount: 1,
      });
      await runtime.warmup();
      return runtime;
    })();
  }

  return runtimePromise;
}

export async function synthesizeWithMossTts(
  text: string,
  options: {
    abortSignal?: AbortSignal;
  } = {},
): Promise<TtsSynthesisResult> {
  const startedAt = performance.now();
  const runtime = await getRuntime();
  const voices = runtime.listBuiltinVoices() as BuiltinVoice[];
  const selectedVoice = chooseVoice(voices);
  const chunks: TtsAudioChunk[] = [];

  if (!selectedVoice) {
    throw new Error("No OpenMOSS builtin voices are available");
  }

  const result = await runtime.synthesizeVoiceClone({
    text,
    voiceName: selectedVoice.voice,
    streaming: false,
    enableWeTextProcessing: false,
    isCancelled: () => options.abortSignal?.aborted ?? false,
    onAudioChunk(chunk: {
      channels: number;
      chunkData: Float32Array[];
      sampleRate: number;
    }) {
      chunks.push({
        channels: chunk.channels,
        chunkData: chunk.chunkData,
        sampleRate: chunk.sampleRate,
      });
    },
  });

  if (options.abortSignal?.aborted) {
    throw new DOMException("Operation aborted", "AbortError");
  }

  const emittedChunks = chunks.flatMap((entry) => entry.chunkData).reduce(
    (count, channel) => count + channel.length,
    0,
  );

  if (!result.outputs.length || !chunks.length || emittedChunks <= 0) {
    throw new Error("OpenMOSS did not emit any audio");
  }

  return {
    durationMs: Math.round(performance.now() - startedAt),
    text,
    voice: selectedVoice.voice,
    chunks,
  };
}
